import {
  forwardRef,
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import { cx } from './cx';

type C1ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

interface C1ButtonBase {
  children: ReactNode;
  variant?: C1ButtonVariant;
  loading?: boolean;
  loadingLabel?: string;
  className?: string;
}

type AsButton = C1ButtonBase &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof C1ButtonBase> & {
    href?: undefined;
    to?: undefined;
  };

type AsAnchor = C1ButtonBase &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof C1ButtonBase | 'href'> & {
    href: string;
    to?: undefined;
  };

type AsRouterLink = C1ButtonBase &
  Omit<LinkProps, keyof C1ButtonBase | 'to'> & {
    to: LinkProps['to'];
    href?: undefined;
  };

export type C1ButtonProps = AsButton | AsAnchor | AsRouterLink;

function variantClass(variant: C1ButtonVariant) {
  if (variant === 'secondary') return 'c1-button-secondary';
  if (variant === 'ghost') return 'c1-button-ghost';
  if (variant === 'danger') return 'c1-button-danger';
  return undefined;
}

/**
 * Primary / secondary / ghost actions — same chrome as the login theme buttons.
 * Renders a `<button>`, `<a>`, or react-router `<Link>` depending on props.
 */
export const C1Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, C1ButtonProps>(
  function C1Button(props, ref) {
    const {
      children,
      variant = 'primary',
      loading = false,
      loadingLabel = 'Loading…',
      className,
    } = props;
    const classes = cx('c1-button', variantClass(variant), className);
    const content = (
      <>
        {loading ? <span className="c1-buttonSpinner" aria-hidden="true" /> : null}
        <span>{loading ? loadingLabel : children}</span>
      </>
    );

    if ('to' in props && props.to !== undefined) {
      const {
        children: _c,
        variant: _v,
        loading: _l,
        loadingLabel: _ll,
        className: _cl,
        to,
        href: _h,
        ...linkRest
      } = props;
      return (
        <Link
          ref={ref as React.Ref<HTMLAnchorElement>}
          to={to}
          className={classes}
          aria-busy={loading || undefined}
          {...linkRest}
        >
          {content}
        </Link>
      );
    }

    if ('href' in props && typeof props.href === 'string') {
      const {
        children: _c,
        variant: _v,
        loading: _l,
        loadingLabel: _ll,
        className: _cl,
        href,
        to: _t,
        ...anchorRest
      } = props;
      return (
        <a
          ref={ref as React.Ref<HTMLAnchorElement>}
          href={href}
          className={classes}
          aria-busy={loading || undefined}
          {...anchorRest}
        >
          {content}
        </a>
      );
    }

    const {
      children: _c,
      variant: _v,
      loading: _l,
      loadingLabel: _ll,
      className: _cl,
      type = 'button',
      disabled,
      ...buttonRest
    } = props as AsButton;

    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type={type}
        className={classes}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...buttonRest}
      >
        {content}
      </button>
    );
  },
);
